import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowRight, Ban, RotateCcw } from 'lucide-react';
import { LayoutGroup, motion } from 'framer-motion';
import type { ExceptionLineItem, ProductOption, UnitOption } from '@/api/cockpit';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  fieldReasonKey,
  formatOptionalCurrency,
  resolveSelectedUnitDisplay,
  sanitizeQuantityInput,
  shouldAllowQuantityKey,
} from '../helpers';
import type { FieldReasonControls } from './FieldTileGrid';
import { FieldReasonPopover } from './FieldReasonPopover';
import { InfoHoverCard } from './InfoHoverCard';
import { MissingFeedbackBadge } from './MissingFeedbackBadge';
import { ProductSelector } from './ProductSelector';

export interface ProductPickerState {
  productPopoverLine: number | null;
  onProductPopoverLineChange: (lineNumber: number | null) => void;
  searchValue: string;
  debouncedSearchValue: string;
  onSearchChange: (value: string) => void;
  searchResults: readonly ProductOption[];
  selectedProducts: Record<number, string>;
  onProductSelect: (lineNumber: number, itemNumber: string) => void;
}

export interface LineItemsTableProps {
  lineItems: readonly ExceptionLineItem[];
  unitOptions: readonly UnitOption[];
  disabled: boolean;
  productPicker: ProductPickerState;
  selectedQuantities: Record<number, string>;
  excludedLines: Record<number, { category: string; reason: string }>;
  onCommitLineExclusion: (lineNumber: number, category: string, reason: string) => void;
  onRemoveLineExclusion: (lineNumber: number) => void;
  onQuantityChange: (lineNumber: number, value: string) => void;
  selectedUnits: Record<number, string>;
  onUnitChange: (lineNumber: number, value: string) => void;
  /** ADO #700: reason capture wiring. */
  reasonControls: FieldReasonControls;
}

export function LineItemsTable(props: LineItemsTableProps): JSX.Element {
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [hiddenFlagged, setHiddenFlagged] = useState<ReadonlySet<number>>(new Set());
  // When the entire table has scrolled above the viewport, flip the indicator
  // from the bottom edge to the top edge so it stays near the work area.
  const [scrolledPastTable, setScrolledPastTable] = useState(false);

  // Track whether the sentinel (placed right after the last row) is above
  // the viewport top. IntersectionObserver only fires on threshold crossings
  // and proved flaky for a zero-height sentinel -- a passive scroll listener
  // in the capture phase reads getBoundingClientRect on every frame and
  // works regardless of which ancestor is the actual scrolling container.
  useEffect(() => {
    const target = sentinelRef.current;
    if (!target) return;
    let rafId = 0;
    function update(): void {
      rafId = 0;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      setScrolledPastTable(rect.bottom < 0);
    }
    function schedule(): void {
      if (rafId !== 0) return;
      rafId = requestAnimationFrame(update);
    }
    update();
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    return () => {
      document.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      if (rafId !== 0) cancelAnimationFrame(rafId);
    };
  }, []);

  const flaggedLineNumbers = useMemo(
    () =>
      props.lineItems
        .filter((line) => line.needsReview === true)
        .map((line) => line.lineNumber),
    [props.lineItems],
  );

  // Track which flagged rows are out of viewport so the sticky indicator can
  // surface them. We only observe rows that are actually flagged; rendering
  // changes (e.g. a row's needsReview flipping false) tear down + rebuild the
  // observer via the dependency on flaggedLineNumbers.
  useEffect(() => {
    if (flaggedLineNumbers.length === 0) {
      setHiddenFlagged(new Set());
      return;
    }
    if (typeof IntersectionObserver === 'undefined') return;

    const initial = new Set<number>();
    const observer = new IntersectionObserver(
      (entries) => {
        setHiddenFlagged((prev) => {
          const next = new Set(prev);
          for (const entry of entries) {
            const raw = (entry.target as HTMLElement).dataset.lineNumber;
            if (!raw) continue;
            const lineNumber = Number(raw);
            if (entry.isIntersecting) next.delete(lineNumber);
            else next.add(lineNumber);
          }
          return next;
        });
      },
      // Shrink the viewport on whichever edge the indicator currently
      // covers, so rows hidden behind it count as hidden. Indicator height
      // is ~40px; 64px gives a generous margin.
      {
        root: null,
        rootMargin: scrolledPastTable
          ? '-64px 0px 0px 0px'
          : '0px 0px -64px 0px',
        threshold: 0,
      },
    );

    for (const lineNumber of flaggedLineNumbers) {
      const row = rowRefs.current.get(lineNumber);
      if (!row) continue;
      observer.observe(row);
      // Seed hidden state from current bounding box -- the observer fires
      // asynchronously so the indicator would briefly flash empty otherwise.
      const rect = row.getBoundingClientRect();
      const viewHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.bottom < 0 || rect.top > viewHeight - 64) initial.add(lineNumber);
    }
    setHiddenFlagged(initial);
    return () => observer.disconnect();
  }, [flaggedLineNumbers, scrolledPastTable]);

  function scrollToLine(lineNumber: number): void {
    const row = rowRefs.current.get(lineNumber);
    row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  const hiddenSorted = useMemo(
    () => [...hiddenFlagged].sort((a, b) => a - b),
    [hiddenFlagged],
  );
  const activeLineCount = props.lineItems.length - Object.keys(props.excludedLines).length;

  return (
    <LayoutGroup>
      <div className="mt-6 space-y-3">
        <div>
          <div className="text-sm font-semibold text-foreground">Line Items</div>
          <div className="text-xs text-muted-foreground">
            Products, quantities, units, and totals from the order.
          </div>
        </div>
        {props.lineItems.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No line items are available for this order.
          </div>
        ) : (
          <div className="max-w-full overflow-x-auto rounded-md border">
            <table className="min-w-[1120px] w-full text-sm">
              <thead className="bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Line</th>
                  <th className="px-3 py-2 font-medium">Extracted Product</th>
                  <th className="px-3 py-2 font-medium text-center">Map</th>
                  <th className="px-3 py-2 font-medium">Matched Product</th>
                  <th className="px-3 py-2 font-medium">Quantity</th>
                  <th className="px-3 py-2 font-medium">Unit</th>
                  <th className="px-3 py-2 font-medium">Unit Price</th>
                  <th className="px-3 py-2 font-medium">Line Total</th>
                  <th className="px-3 py-2 font-medium">Exclude</th>
                  <th className="px-3 py-2 font-medium">Review</th>
                </tr>
              </thead>
              <tbody>
                {props.lineItems.map((line) => (
                  <LineItemRow
                    key={`${line.lineNumber}-${line.itemCode ?? 'unknown'}`}
                    rowRef={(el) => {
                      if (el) rowRefs.current.set(line.lineNumber, el);
                      else rowRefs.current.delete(line.lineNumber);
                    }}
                    line={line}
                    unitOptions={props.unitOptions}
                    disabled={props.disabled}
                    productPicker={props.productPicker}
                    quantityValue={props.selectedQuantities[line.lineNumber] ?? ''}
                    excluded={props.excludedLines[line.lineNumber] ?? null}
                    canExclude={activeLineCount > 1 || Boolean(line.manualLineId) || props.excludedLines[line.lineNumber] !== undefined}
                    onCommitLineExclusion={(category, reason) =>
                      props.onCommitLineExclusion(line.lineNumber, category, reason)
                    }
                    onRemoveLineExclusion={() => props.onRemoveLineExclusion(line.lineNumber)}
                    onQuantityChange={(value) => props.onQuantityChange(line.lineNumber, value)}
                    unitValue={props.selectedUnits[line.lineNumber] ?? line.matchedUnitValue ?? ''}
                    onUnitChange={(value) => props.onUnitChange(line.lineNumber, value)}
                    showChip={line.needsReview === true && !hiddenFlagged.has(line.lineNumber)}
                    reasonControls={props.reasonControls}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Sentinel for detecting "scrolled past the table" so the floating
           indicator can flip from bottom-of-viewport to top-of-viewport.
           Tiny but non-zero height so the bounding rect is well-defined
           even when the sentinel is right at the viewport edge. */}
        <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
        <FlaggedLinesIndicator
          lines={hiddenSorted}
          onScrollTo={scrollToLine}
          position={scrolledPastTable ? 'top' : 'bottom'}
        />
      </div>
    </LayoutGroup>
  );
}

function LineItemRow(props: {
  rowRef: (el: HTMLTableRowElement | null) => void;
  line: ExceptionLineItem;
  unitOptions: readonly UnitOption[];
  disabled: boolean;
  productPicker: ProductPickerState;
  quantityValue: string;
  excluded: { category: string; reason: string } | null;
  canExclude: boolean;
  onCommitLineExclusion: (category: string, reason: string) => void;
  onRemoveLineExclusion: () => void;
  onQuantityChange: (value: string) => void;
  unitValue: string;
  onUnitChange: (value: string) => void;
  showChip: boolean;
  reasonControls: FieldReasonControls;
}): JSX.Element {
  const { line, productPicker, disabled, showChip, reasonControls } = props;
  const lineFlagged = line.needsReview === true;
  const lineExcluded = props.excluded !== null;
  const rowDisabled = disabled || lineExcluded;

  const productReasonKey = fieldReasonKey({ fieldName: 'product', lineNumber: line.lineNumber });
  const unitReasonKey = fieldReasonKey({ fieldName: 'unit_of_measure', lineNumber: line.lineNumber });
  const quantityReasonKey = fieldReasonKey({ fieldName: 'quantity', lineNumber: line.lineNumber });

  function handleProductSelect(itemNumber: string): void {
    productPicker.onProductSelect(line.lineNumber, itemNumber);
    productPicker.onProductPopoverLineChange(null);
    if (itemNumber !== (line.matchedProductCode ?? '')) {
      reasonControls.requestReasonImmediate(productReasonKey, `Line ${line.lineNumber} Product`);
    }
  }

  function handleUnitChange(value: string): void {
    props.onUnitChange(value);
    if (value !== (line.matchedUnitValue ?? '')) {
      reasonControls.requestReasonImmediate(unitReasonKey, `Line ${line.lineNumber} Unit`);
    }
  }

  function handleQuantityChange(value: string): void {
    const sanitized = sanitizeQuantityInput(value);
    props.onQuantityChange(sanitized);
    const previousQuantity =
      line.quantity === null || line.quantity === undefined ? '' : String(line.quantity);
    if (sanitized !== previousQuantity) {
      reasonControls.requestReasonDebounced({
        reasonKey: quantityReasonKey,
        label: `Line ${line.lineNumber} Quantity`,
        pendingValue: sanitized,
        previousValue: previousQuantity,
      });
    }
  }
  return (
    <tr
      ref={props.rowRef}
      data-line-number={line.lineNumber}
      className={cn(
        'border-t scroll-mt-24',
        lineFlagged && 'bg-destructive/5',
        lineExcluded && 'bg-muted/40 opacity-70',
      )}
    >
      <td className="px-3 py-2 align-middle">
        {/*
          Both branches render an identically-sized pill so toggling the
          flagged state does not reflow the table. The flagged branch adds
          the red border + framer-motion layoutId so it morphs into / out
          of the bottom indicator's "Line N" pill.
        */}
        {showChip ? (
          <motion.span
            layoutId={`flagged-line-${line.lineNumber}`}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            className="inline-flex h-5 min-w-[2rem] items-center justify-center rounded-full bg-destructive px-2 text-[11px] font-semibold tabular-nums text-destructive-foreground"
          >
            {line.lineNumber}
          </motion.span>
        ) : (
          <span className="inline-flex h-5 min-w-[2rem] items-center justify-center px-2 text-[11px] tabular-nums text-muted-foreground">
            {line.lineNumber}
          </span>
        )}
        {line.manualLineId && <span className="mt-1 block text-[10px] text-muted-foreground" title={`Added manually by ${line.manualAddedBy ?? 'the operator'}`}>Manual</span>}
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="rounded-md border bg-background px-3 py-2 text-sm">
          <ExtractedProductField label="Item Code" value={line.itemCode} />
          <ExtractedProductField label="Supplier Part" value={line.supplierPartNumber} />
          <ExtractedProductField label="Description" value={line.description} />
        </div>
      </td>
      <td className="px-3 py-2 align-middle text-center">
        <ArrowRight className="mx-auto h-4 w-4 text-muted-foreground" />
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="flex min-h-[5.5rem] items-center">
          <ProductSelector
            line={line}
            isOpen={productPicker.productPopoverLine === line.lineNumber}
            searchValue={productPicker.searchValue}
            debouncedSearchValue={productPicker.debouncedSearchValue}
            searchResults={productPicker.searchResults}
            selectedProducts={productPicker.selectedProducts}
            disabled={rowDisabled}
            destructive={lineFlagged}
            onOpenChange={(open) => {
              if (rowDisabled) return;
              productPicker.onProductPopoverLineChange(open ? line.lineNumber : null);
              productPicker.onSearchChange('');
            }}
            onSearchChange={productPicker.onSearchChange}
            onSelect={(itemNumber) => {
              if (rowDisabled) return;
              handleProductSelect(itemNumber);
            }}
          />
        </div>
        <LineItemReasonAffordance
          reasonKey={productReasonKey}
          label={`Line ${line.lineNumber} Product`}
          reasonControls={reasonControls}
        />
      </td>
      <td className="px-3 py-2 align-middle">
        <Input
          value={props.quantityValue}
          title={props.quantityValue}
          type="text"
          inputMode="decimal"
          pattern="[0-9]*[.,]?[0-9]*"
          disabled={rowDisabled}
          onKeyDown={(event) => {
            if (
              !shouldAllowQuantityKey(
                event.key,
                props.quantityValue,
                event.currentTarget.selectionStart,
                event.currentTarget.selectionEnd,
                event.ctrlKey || event.metaKey,
              )
            ) {
              event.preventDefault();
            }
          }}
          onChange={(event) => {
            if (rowDisabled) return;
            handleQuantityChange(event.target.value);
          }}
        />
        <LineItemReasonAffordance
          reasonKey={quantityReasonKey}
          label={`Line ${line.lineNumber} Quantity`}
          reasonControls={reasonControls}
        />
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="min-w-[4.5rem] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Extracted
            </div>
            <Input
              value={line.extractedUnitValue ?? ''}
              title={line.extractedUnitValue ?? ''}
              readOnly
              disabled
              className="w-24"
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="min-w-[4.5rem] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Matched
            </div>
            <Select
              value={props.unitValue}
              onValueChange={(value) => {
                if (rowDisabled) return;
                handleUnitChange(value);
              }}
              disabled={rowDisabled}
            >
              <SelectTrigger
                title={resolveSelectedUnitDisplay(props.unitOptions, props.unitValue)}
                className="w-28 bg-background"
              >
                <SelectValue placeholder="Select unit" />
              </SelectTrigger>
              <SelectContent>
                {props.unitOptions.map((option) => (
                  <SelectItem key={`uom-${option.symbol}`} value={option.symbol}>
                    {option.display}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <LineItemReasonAffordance
            reasonKey={unitReasonKey}
            label={`Line ${line.lineNumber} Unit`}
            reasonControls={reasonControls}
          />
        </div>
      </td>
      <td className="min-w-[240px] px-3 py-2 align-middle">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="min-w-[3rem] text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              PO
            </div>
            <span
              className={cn(
                'text-sm',
                line.priceStatus === 'mismatch' && 'font-semibold text-destructive',
              )}
            >
              {formatOptionalCurrency(line.poUnitPrice ?? null, line.poUnitPriceCurrency)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="min-w-[6rem] text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
              title="D365 trade-agreement price"
            >
              Agreement
            </div>
            <span
              className={cn(
                'text-sm',
                line.priceStatus === 'mismatch' && 'font-semibold text-destructive',
              )}
            >
              {formatOptionalCurrency(line.d365UnitPrice ?? null, line.d365UnitPriceCurrency)}
            </span>
          </div>
          {line.priceReviewMessage ? (
            <div className="flex max-w-64 items-start gap-1 text-xs text-amber-800">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              <span>{line.priceReviewMessage}</span>
            </div>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-2 align-middle">{formatOptionalCurrency(line.lineTotal)}</td>
      <td className="px-3 py-2 align-middle">
        <LineExclusionPopover
          disabled={disabled || !props.canExclude}
          excluded={props.excluded}
          onCommit={props.onCommitLineExclusion}
          onRemove={props.onRemoveLineExclusion}
        />
      </td>
      <td className="px-3 py-2 align-middle">
        {line.reviewMessage ? (
          <InfoHoverCard message={line.reviewMessage} />
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </td>
    </tr>
  );
}

const LINE_EXCLUSION_CATEGORIES = [
  'Not an order line',
  'Terms or insurance text',
  'Duplicate line',
  'Wrongly matched by OCR',
] as const;

function LineExclusionPopover(props: {
  disabled: boolean;
  excluded: { category: string; reason: string } | null;
  onCommit: (category: string, reason: string) => void;
  onRemove: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<string>(props.excluded?.category ?? LINE_EXCLUSION_CATEGORIES[0]);
  const [reason, setReason] = useState(props.excluded?.reason ?? '');

  useEffect(() => {
    setCategory(props.excluded?.category ?? LINE_EXCLUSION_CATEGORIES[0]);
    setReason(props.excluded?.reason ?? '');
  }, [props.excluded]);

  if (props.excluded) {
    return (
      <Button
        type="button"
        size="icon"
        variant="ghost"
        title="Undo line exclusion"
        aria-label="Undo line exclusion"
        onClick={props.onRemove}
        disabled={props.disabled}
      >
        <RotateCcw className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          title={
            props.disabled
              ? 'At least one line must remain valid'
              : 'Mark line as wrongly extracted'
          }
          aria-label="Mark line as wrongly extracted"
          disabled={props.disabled}
        >
          <Ban className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <div>
          <div className="text-sm font-semibold">Exclude line</div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="line-exclusion-category">Reason type</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger id="line-exclusion-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LINE_EXCLUSION_CATEGORIES.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="line-exclusion-reason">Feedback</Label>
          <Textarea
            id="line-exclusion-reason"
            value={reason}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Example: insurance coverage limit, not a product ordered by the customer"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={reason.trim().length < 8}
            onClick={() => {
              props.onCommit(category, reason);
              setOpen(false);
            }}
          >
            Exclude
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function LineItemReasonAffordance(props: {
  reasonKey: string;
  label: string;
  reasonControls: FieldReasonControls;
}): JSX.Element | null {
  const { reasonKey, label, reasonControls } = props;
  if (!reasonControls.missingReasonKeys.has(reasonKey)) return null;
  return (
    <div className="mt-2">
      <FieldReasonPopover
        reasonKey={reasonKey}
        label={label}
        open={reasonControls.openReasonKey === reasonKey}
        draft={reasonControls.fieldReasonDrafts[reasonKey] ?? ''}
        onDraftChange={(draft) => reasonControls.setReasonDraft(reasonKey, draft)}
        onCommit={(text) => reasonControls.commitReason(reasonKey, text)}
        onDismiss={() => reasonControls.dismissReasonPopover()}
        anchor={
          <span>
            <MissingFeedbackBadge
              onClick={() => reasonControls.requestReasonImmediate(reasonKey, label)}
            />
          </span>
        }
      />
    </div>
  );
}

function ExtractedProductField(props: { label: string; value: string | null | undefined }): JSX.Element {
  return (
    <div>
      <span className="mr-1 inline-block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {props.label}
      </span>
      <span className="text-foreground">{props.value ?? 'None'}</span>
    </div>
  );
}

function FlaggedLinesIndicator(props: {
  lines: readonly number[];
  onScrollTo: (lineNumber: number) => void;
  position: 'top' | 'bottom';
}): JSX.Element | null {
  if (props.lines.length === 0) return null;
  // When pinned to the top, leave clearance for the sticky page header
  // (top-0 z-30, ~48px tall). When pinned to the bottom, sit against the
  // bottom edge with comfortable padding.
  const placement =
    props.position === 'top' ? 'top-0 pt-14' : 'bottom-0 pb-4';
  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 z-40 flex justify-center',
        placement,
      )}
    >
      <div className="pointer-events-auto flex max-w-full flex-wrap items-center gap-2 rounded-full border-2 border-destructive bg-background px-4 py-2 text-xs text-foreground shadow-lg">
        <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
        <span className="font-medium">Needs review:</span>
        {props.lines.map((line) => (
          <motion.button
            key={line}
            layoutId={`flagged-line-${line}`}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            type="button"
            onClick={() => props.onScrollTo(line)}
            className="rounded-full bg-destructive px-2 py-0.5 font-semibold text-destructive-foreground transition-colors hover:brightness-110"
          >
            Line {line}
          </motion.button>
        ))}
      </div>
    </div>
  );
}
